import React, { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../shadcn/ui/card";
import { Button } from "../../shadcn/ui/button";
import { Input } from "../../shadcn/ui/input";
import { Label } from "../../shadcn/ui/label";
import { Textarea } from "../../shadcn/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../shadcn/ui/dialog";
import { Badge } from "../../shadcn/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../shadcn/ui/tabs";
import { showApiMessage } from "../../utils/messageHandler";
import logger from "../../utils/logger";
import { Calendar, FileText, Settings, Plus, Edit, Trash2, Eye } from "lucide-react";
import policyManagementService from "../../services/policyManagementService";

const PolicyManagement = () => {
  const [loading, setLoading] = useState(false);
  const [cancellationPolicies, setCancellationPolicies] = useState([]);
  const [termsAndConditions, setTermsAndConditions] = useState([]);
  const [selectedPolicy, setSelectedPolicy] = useState(null);
  const [selectedTerms, setSelectedTerms] = useState(null);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isViewDialogOpen, setIsViewDialogOpen] = useState(false);
  const [dialogType, setDialogType] = useState(""); // "cancellation" or "terms"
  const [formData, setFormData] = useState({
    // Common fields
    name: "",
    description: "",
    isActive: true,

    // Cancellation policy specific
    gracePeriodHours: "",
    refundPercentage: "",

    // Terms & Conditions specific
    title: "",
    content: "",
    version: "",
    type: "GENERAL",
    effectiveFrom: "",
    effectiveTo: "",
  });

  // Load data on component mount
  useEffect(() => {
    loadCancellationPolicies();
    loadTermsAndConditions();
  }, []);

  const loadCancellationPolicies = async () => {
    try {
      setLoading(true);
      const response = await policyManagementService.getCancellationPolicies(0, 100);
      setCancellationPolicies(response.content || []);
    } catch (error) {
      logger.error("Error loading cancellation policies:", error);
      showApiMessage(error, "load_policies");
      // Set empty array on error
      setCancellationPolicies([]);
    } finally {
      setLoading(false);
    }
  };

  const loadTermsAndConditions = async () => {
    try {
      setLoading(true);
      const response = await policyManagementService.getTermsAndConditions(0, 100);
      setTermsAndConditions(response.content || []);
    } catch (error) {
      logger.error("Error loading terms and conditions:", error);
      showApiMessage(error, "load_terms");
      // Set empty array on error
      setTermsAndConditions([]);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = type => {
    setDialogType(type);
    setFormData({
      name: "",
      description: "",
      isActive: true,
      gracePeriodHours: "",
      refundPercentage: "",
      title: "",
      content: "",
      version: "1.0",
      type: "GENERAL",
      effectiveFrom: "",
      effectiveTo: "",
    });
    setIsCreateDialogOpen(true);
  };

  const handleEdit = (item, type) => {
    setDialogType(type);
    setSelectedPolicy(type === "cancellation" ? item : null);
    setSelectedTerms(type === "terms" ? item : null);

    if (type === "cancellation") {
      setFormData({
        name: item.name || "",
        description: item.description || "",
        isActive: true, // Default for cancellation policies
        gracePeriodHours: item.hoursBefore?.toString() || "",
        refundPercentage: item.refundPercentage?.toString() || "",
        title: "",
        content: "",
        version: "",
        type: "GENERAL",
        effectiveFrom: "",
        effectiveTo: "",
      });
    } else {
      setFormData({
        name: "",
        description: "",
        isActive: true, // Default for cancellation policies
        gracePeriodHours: "",
        refundPercentage: "",
        title: item.title || "",
        content: item.content || "",
        version: "1.0", // Default version
        type: "GENERAL", // Default type
        effectiveFrom: "", // Not used in API
        effectiveTo: "", // Not used in API
      });
    }
    setIsEditDialogOpen(true);
  };

  const handleView = (item, type) => {
    setDialogType(type);
    setSelectedPolicy(type === "cancellation" ? item : null);
    setSelectedTerms(type === "terms" ? item : null);
    setIsViewDialogOpen(true);
  };

  const handleSave = async () => {
    try {
      setLoading(true);

      if (dialogType === "cancellation") {
        const policyData = {
          name: formData.name,
          description: formData.description,
          hoursBefore: parseFloat(formData.gracePeriodHours) || 0,
          refundPercentage: parseFloat(formData.refundPercentage) || 0,
        };

        if (isCreateDialogOpen) {
          await policyManagementService.createCancellationPolicy(policyData);
        } else {
          await policyManagementService.updateCancellationPolicy(selectedPolicy.id, policyData);
        }

        // Reload the list
        await loadCancellationPolicies();
      } else {
        const termsData = {
          title: formData.title,
          content: formData.content,
        };

        if (isCreateDialogOpen) {
          await policyManagementService.createTerms(termsData);
        } else {
          await policyManagementService.updateTerms(selectedTerms.id, termsData);
        }

        // Reload the list
        await loadTermsAndConditions();
      }

      setIsCreateDialogOpen(false);
      setIsEditDialogOpen(false);
      showApiMessage({ message: "Policy saved successfully" }, "success");
    } catch (error) {
      logger.error("Error saving policy:", error);
      showApiMessage(error, "save_policy");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (item, type) => {
    if (!window.confirm("Are you sure you want to delete this policy?")) {
      return;
    }

    try {
      setLoading(true);

      if (type === "cancellation") {
        await policyManagementService.deleteCancellationPolicy(item.id);
        await loadCancellationPolicies();
      } else {
        await policyManagementService.deleteTerms(item.id);
        await loadTermsAndConditions();
      }

      showApiMessage({ message: "Policy deleted successfully" }, "success");
    } catch (error) {
      logger.error("Error deleting policy:", error);
      showApiMessage(error, "delete_policy");
    } finally {
      setLoading(false);
    }
  };

  const createSampleCancellationPolicy = async () => {
    try {
      setLoading(true);

      const samplePolicy = {
        name: "Standard 24-Hour Cancellation",
        description:
          "Allows customers to cancel their bookings up to 24 hours before the scheduled start time with a full refund.",
        hoursBefore: 24,
        refundPercentage: 100,
      };

      await policyManagementService.createCancellationPolicy(samplePolicy);
      await loadCancellationPolicies();

      showApiMessage({ message: "Sample cancellation policy created successfully!" }, "success");
    } catch (error) {
      logger.error("Error creating sample policy:", error);
      showApiMessage(error, "create_sample_policy");
    } finally {
      setLoading(false);
    }
  };

  const formatDate = dateString => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const renderPolicyCard = (policy, type) => (
    <Card key={policy.id} className="mb-4">
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <CardTitle className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              {type === "cancellation" ? <Calendar className="w-5 h-5" /> : <FileText className="w-5 h-5" />}
              {type === "cancellation" ? policy.name : policy.title}
            </CardTitle>
            {type === "cancellation" && policy.description && (
              <CardDescription className="mt-1 text-gray-600">{policy.description}</CardDescription>
            )}
            {type === "terms" && (
              <CardDescription className="mt-1 text-gray-600">{policy.active ? "Active" : "Inactive"}</CardDescription>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Badge variant={type === "cancellation" ? "default" : policy.active ? "default" : "secondary"}>
              {type === "cancellation" ? "Policy" : policy.active ? "Active" : "Inactive"}
            </Badge>
          </div>
        </div>
      </CardHeader>
      <CardContent className="pt-0">
        <div className="space-y-3">
          {type === "cancellation" && (
            <div className="flex gap-4 text-sm text-gray-600">
              <span>
                <strong>Grace Period:</strong> {policy.hoursBefore} hours
              </span>
              <span>
                <strong>Refund:</strong> {policy.refundPercentage}%
              </span>
            </div>
          )}
          {type === "terms" && (
            <div className="flex gap-4 text-sm text-gray-600">
              <span>
                <strong>Status:</strong> {policy.active ? "Active" : "Inactive"}
              </span>
              {policy.createdAt && (
                <span>
                  <strong>Created:</strong> {formatDate(policy.createdAt)}
                </span>
              )}
            </div>
          )}
          {policy.content && <p className="text-sm text-gray-700 line-clamp-2">{policy.content}</p>}
          <div className="flex justify-between items-center pt-2">
            <span className="text-xs text-gray-500">
              Created: {policy.createdAt ? formatDate(policy.createdAt) : "N/A"}
            </span>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={() => handleView(policy, type)}>
                <Eye className="w-4 h-4" />
              </Button>
              <Button variant="outline" size="sm" onClick={() => handleEdit(policy, type)}>
                <Edit className="w-4 h-4" />
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleDelete(policy, type)}
                className="text-red-600 hover:text-red-700"
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );

  const renderDialog = () => {
    const isView = isViewDialogOpen;

    return (
      <Dialog
        open={isCreateDialogOpen || isEditDialogOpen || isViewDialogOpen}
        onOpenChange={() => {
          setIsCreateDialogOpen(false);
          setIsEditDialogOpen(false);
          setIsViewDialogOpen(false);
        }}
      >
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {isView ? "View" : isEditDialogOpen ? "Edit" : "Create"}{" "}
              {dialogType === "cancellation" ? "Cancellation Policy" : "Terms & Conditions"}
            </DialogTitle>
            <DialogDescription>
              {isView ? "View the policy details" : "Configure the policy settings and content"}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {dialogType === "cancellation" && (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="name">Name</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={e => setFormData({ ...formData, name: e.target.value })}
                    disabled={isView}
                  />
                </div>
                <div>
                  <Label htmlFor="description">Description</Label>
                  <Input
                    id="description"
                    value={formData.description}
                    onChange={e => setFormData({ ...formData, description: e.target.value })}
                    disabled={isView}
                  />
                </div>
              </div>
            )}

            {dialogType === "terms" && (
              <div>
                <Label htmlFor="title">Title</Label>
                <Input
                  id="title"
                  value={formData.title}
                  onChange={e => setFormData({ ...formData, title: e.target.value })}
                  disabled={isView}
                  placeholder="e.g., General Terms for Event Spaces"
                />
              </div>
            )}

            {dialogType === "cancellation" && (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="gracePeriodHours">Grace Period (Hours)</Label>
                  <Input
                    id="gracePeriodHours"
                    type="number"
                    value={formData.gracePeriodHours}
                    onChange={e => setFormData({ ...formData, gracePeriodHours: e.target.value })}
                    placeholder="e.g., 24"
                    disabled={isView}
                  />
                </div>
                <div>
                  <Label htmlFor="refundPercentage">Refund Percentage (%)</Label>
                  <Input
                    id="refundPercentage"
                    type="number"
                    min="0"
                    max="100"
                    value={formData.refundPercentage}
                    onChange={e => setFormData({ ...formData, refundPercentage: e.target.value })}
                    placeholder="e.g., 100"
                    disabled={isView}
                  />
                </div>
              </div>
            )}

            <div>
              <Label htmlFor="content">Content</Label>
              <Textarea
                id="content"
                value={formData.content}
                onChange={e => setFormData({ ...formData, content: e.target.value })}
                rows={8}
                disabled={isView}
                className="resize-none"
              />
            </div>
          </div>

          {!isView && (
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => {
                  setIsCreateDialogOpen(false);
                  setIsEditDialogOpen(false);
                }}
              >
                Cancel
              </Button>
              <Button onClick={handleSave} disabled={loading}>
                {loading ? "Saving..." : isEditDialogOpen ? "Update" : "Create"}
              </Button>
            </DialogFooter>
          )}
        </DialogContent>
      </Dialog>
    );
  };

  return (
    <div className="container mx-auto px-4 py-6">
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
              <Settings className="w-8 h-8 text-blue-600" />
              Policy Management
            </h1>
            <p className="text-gray-600 mt-2">Manage cancellation policies and terms & conditions for your platform</p>
          </div>
        </div>
      </div>

      <Tabs defaultValue="cancellation" className="space-y-6">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="cancellation" className="flex items-center gap-2">
            <Calendar className="w-4 h-4" />
            Cancellation Policies
          </TabsTrigger>
          <TabsTrigger value="terms" className="flex items-center gap-2">
            <FileText className="w-4 h-4" />
            Terms & Conditions
          </TabsTrigger>
        </TabsList>

        <TabsContent value="cancellation" className="space-y-6">
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-semibold text-gray-900">Cancellation Policies</h2>
            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={createSampleCancellationPolicy}
                className="flex items-center gap-2"
                disabled={loading}
              >
                <Calendar className="w-4 h-4" />
                Add Sample Policy
              </Button>
              <Button onClick={() => handleCreate("cancellation")} className="flex items-center gap-2">
                <Plus className="w-4 h-4" />
                Add Policy
              </Button>
            </div>
          </div>

          {loading ? (
            <div className="text-center py-8">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              <p className="mt-2 text-gray-600">Loading policies...</p>
            </div>
          ) : (
            <div className="space-y-4">
              {cancellationPolicies.length === 0 ? (
                <Card>
                  <CardContent className="text-center py-8">
                    <Calendar className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">No Cancellation Policies</h3>
                    <p className="text-gray-600 mb-4">Get started by creating your first cancellation policy.</p>
                    <Button onClick={() => handleCreate("cancellation")} className="flex items-center gap-2">
                      <Plus className="w-4 h-4" />
                      Create Policy
                    </Button>
                  </CardContent>
                </Card>
              ) : (
                cancellationPolicies.map(policy => renderPolicyCard(policy, "cancellation"))
              )}
            </div>
          )}
        </TabsContent>

        <TabsContent value="terms" className="space-y-6">
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-semibold text-gray-900">Terms & Conditions</h2>
            <Button onClick={() => handleCreate("terms")} className="flex items-center gap-2">
              <Plus className="w-4 h-4" />
              Add Terms
            </Button>
          </div>

          {loading ? (
            <div className="text-center py-8">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              <p className="mt-2 text-gray-600">Loading terms...</p>
            </div>
          ) : (
            <div className="space-y-4">
              {termsAndConditions.length === 0 ? (
                <Card>
                  <CardContent className="text-center py-8">
                    <FileText className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">No Terms & Conditions</h3>
                    <p className="text-gray-600 mb-4">
                      Get started by creating your first terms and conditions document.
                    </p>
                    <Button onClick={() => handleCreate("terms")} className="flex items-center gap-2">
                      <Plus className="w-4 h-4" />
                      Create Terms
                    </Button>
                  </CardContent>
                </Card>
              ) : (
                termsAndConditions.map(terms => renderPolicyCard(terms, "terms"))
              )}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {renderDialog()}
    </div>
  );
};

export default PolicyManagement;
